import React, {useEffect, useState} from 'react';
import ReactDataSheet from 'react-datasheet';
import { makeStyles } from "@material-ui/core/styles";

import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Colors from '../utils/colors';

import { metricYear2Grid, columns, rows, cashFlowRows, performanceCashRows, updateGrid, updateMetricData } from '../utils/impactUtils';
import { formatDate } from '../utils/dateUtils';

const currencyFormatter = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });
const numberFormatter = new Intl.NumberFormat('en-CA', { minimumFractionDigits: 5 });

const useStyles = makeStyles(() => ({
    addYearButton: {
        display: 'block',
        width: '5%',
        backgroundColor: Colors.lightBlue,
        "&:hover": {
            backgroundColor: 'rgba(110, 247, 138, 0.33)',
            color: Colors.indigoBlue
          },
        margin: '1rem'
    },
    changeYearButton: {
        width: '5%',
        margin: '1rem'
    },
    gutterContainer: {
      flexGrow: 1,
      minWidth: 0
    },
    gridContainer: {
        margin: '1rem',
        textTransform: 'uppercase',
      overflowX: 'auto',
    },
    rowHeader: {
        border: '1px solid'
    },
    columnHeader: {
        border: '1px solid',
        padding: '16px 4px',
        fontWeight: 'bold',
        width: '5rem'
    },
    cell: {
        textAlign: 'center',
        verticalAlign: 'middle',
        border: '1px solid',
        padding: '4px'
    },
    selected: {
        border: '1px double rgb(33, 133, 208)',
        boxShadow: 'inset 0 -100px 0 rgba(33, 133, 208, 0.15)'
    }
}));

export function DataGutter({prev, onClick, isNew, disabled }) {
    const classes = useStyles();
    return (
        <>
        {isNew ?
            <Button
                onClick={onClick}
                className={classes.changeYearButton}
                disabled={disabled}
            >
                {prev ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
            </Button> :
            <Button
                onClick={onClick}
                className={classes.addYearButton}
                startIcon={<AddCircleOutlineIcon/>}
                color='primary'
                disabled={disabled}
            >
                ADD YEAR
            </Button>
        }
        </>
    )
}

export function DataGrid({ currentYear, data, onChange, disabled, isCashFlow, formatNumbersAsCurrency, sapNumberFound,
                           isPerformanceCash = false, targetDisabled = false, targetRow= 0}) {
    const classes = useStyles();
    let useRows = rows;
    if (isCashFlow) {
      useRows = cashFlowRows;
    }

    if (isPerformanceCash) {
      // Last minute change means that the data and the label are different.
      useRows = performanceCashRows;
    }

    // Currency has maximum 2 decimal places. Other fields have 5 decimal places.
    let pattern = /^[+-]*[,0-9]*[.]?[0-9]{0,5}$/;
    if (formatNumbersAsCurrency) {
      pattern = /^[$+-]*[,0-9]*[.]?[0-9]{0,2}$/;
    }

    const currencyCharsRegex = /^[0-9$+\-.,]?$/;
    const nonCurrencyCharsRegex = /^[0-9+\-.,]?$/;

    const onKeyPress = (event) => {
      const value = event.key;
      const targetValue = event.target.value;
      let valid;
      if (formatNumbersAsCurrency) {
        valid = currencyCharsRegex.test(value);
      } else {
        valid = nonCurrencyCharsRegex.test(value);
      }

      if (value === '.' && targetValue.length >=1 && targetValue.charAt(targetValue.length-1) === ',') {
        valid = false;
      }

      if (valid) {
        const newValue = targetValue + value;
        valid = pattern.test(newValue);
      }

      if (!valid) {
        event.preventDefault();
      }
      return valid;
    }

    const handleOnChange = (changes) => {
      const change = changes[0];
      if (change.value) {
        let newValue = change.value.replace(/[$,]/g, '');
        change.value = newValue;
      }
      onChange(changes);
    }

    return (
        <div className={classes.gridContainer}>
            <ReactDataSheet
                data={data}
                onCellsChanged={handleOnChange}
                sheetRenderer={props => (
                    <Table style={{tableLayout: 'auto'}} className={props.className}>
                        <TableHead>
                            <TableRow>
                                <th style={{ backgroundColor: '#C4C4C4', border: '1px solid'}}/>
                                {columns.map(col => (<th className={classes.rowHeader} key={col.name}>{col.name}</th>))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.children}
                        </TableBody>
                    </Table>
                )}
                cellRenderer={({styles, selected, editing, updated, onMouseDown, onMouseOver, onDoubleClick, onContextMenu, children, row, col, cell}) => {
                    sapNumberFound = !!sapNumberFound;
                    disabled = !!disabled;


                    // lock cells in the Baseline, Actual and Forecast rows in case SAP project number is set
                    // Also target row is locked when in deliver, submit embed and embed state.
                    let locked = (sapNumberFound && ([0, 2, 3].indexOf(row) !== -1)) || disabled ||
                      (targetDisabled && row === targetRow);

                    if (!sapNumberFound && !disabled) {
                        // lock cells in Actual row for current and future months
                        const isCurrentOrFutureMonth = (currentYear - 1) * 12 + col + 1 >= (parseInt(formatDate('YYYY')) - 1) * 12 + parseInt(formatDate('MM'));
                        // Also target row is locked when in deliver, submit embed and embed state.
                        const isActualForCurrentOrFutureMonth = (row === 2 && isCurrentOrFutureMonth) ||
                          (targetDisabled && row === targetRow);
                        locked = isActualForCurrentOrFutureMonth;
                    }

                    const newStyle = locked ? { backgroundColor: '#C4C4C4' } : { ...styles };
                    // Manually exclude onMouseClick and attributesRenderer (they were throwing errors)
                    const newProps = locked ? { } : { selected, updated, onMouseDown, onMouseOver, onDoubleClick, onContextMenu };
                    const cellClass = `${classes.cell} ${selected ? classes.selected : ''}`;

                    cell.readOnly = locked;

                    return (
                        <TableCell className={cellClass}
                                    style={newStyle}
                                    {...newProps}
                                    onKeyPress={onKeyPress}>
                            {children}
                        </TableCell>
                    );
                }}
                rowRenderer={props => {
                  return (<TableRow>
                    <TableCell className={classes.columnHeader}>
                      {useRows[props.row].name}
                    </TableCell>
                    {props.children}
                  </TableRow>)
                }}
                valueRenderer={(cell, i, j) => {
                    const rawValue = cell.value ? String(cell.value).replace(/[$,]/g, '') : cell.value;
                    if (formatNumbersAsCurrency) {
                      return cell.value ? currencyFormatter.format(rawValue) : cell.value;
                    }

                    return cell.value ? numberFormatter.format(rawValue) : cell.value;
                }}
            />
        </div>
    )
}

export function DataGutters({ currentYear, setOfYears, onYearChange, onNewYear, children, disabled }) {
    const classes = useStyles();
    const onLeft = () => {
        const targetYear = currentYear - 1;
        if (setOfYears.includes(currentYear - 1)) {
            onYearChange(targetYear);
        } else {
            onNewYear(targetYear)
        }
    };

    const onRight = () => {
        const targetYear = currentYear + 1;
        if (setOfYears.includes(currentYear + 1)) {
            onYearChange(targetYear);
        } else {
            onNewYear(targetYear)
        }
    }

    return (
        <div style={{ display: 'flex', width: '100%' }}>
            <DataGutter
                onClick={onLeft}
                isNew={setOfYears.includes(currentYear - 1)}
                disabled={setOfYears.includes(currentYear - 1)? false : disabled}
                prev
            />
            <div className={classes.gutterContainer}>
                {children}
            </div>
            <DataGutter
                onClick={onRight}
                isNew={setOfYears.includes(currentYear + 1)}
                disabled={setOfYears.includes(currentYear + 1)? false : disabled}
            />
        </div>
    );
}

export default function MetricSheet({ impact: { id: valueId, metricYears, metricData, sapNumberFound }, currentYear, onYearChange,
                                      updateMetric, createMetricYear, disabled, isCashFlow = false, formatNumbersAsCurrency = true, targetDisabled }) {

  const [ grid, setGrid ] = useState(metricYear2Grid(metricData[currentYear], isCashFlow));

  useEffect(() => {
    setGrid(metricYear2Grid(metricData[currentYear], isCashFlow));
  }, [ metricData ]);

    const onChange = (changes) => {
        setGrid(updateGrid(grid, changes));
        updateMetric(metricData[currentYear].id, updateMetricData(changes, isCashFlow));
    };

    const changeYear = (year) => {
        onYearChange(year);
        setGrid(metricYear2Grid(metricData[year], isCashFlow))
    };

    return (
        <div style={{display: 'flex'}}>
            <DataGutters
                currentYear={currentYear}
                setOfYears={metricYears}
                onYearChange={changeYear}
                onNewYear={(year) => createMetricYear(valueId, year, () => changeYear(year))}
                disabled={disabled}
            >
                <Typography variant='h5'>
                    {currentYear}
                </Typography>
                <DataGrid
                    currentYear={currentYear}
                    data={grid}
                    onChange={onChange}
                    disabled={disabled}
                    targetDisabled={targetDisabled}
                    isCashFlow={isCashFlow}
                    formatNumbersAsCurrency={formatNumbersAsCurrency}
                    sapNumberFound={sapNumberFound}
                />
            </DataGutters>
        </div>
    )
}
